import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { AppsIcon, GoogleIcon, AppleIcon } from "./icons";
import { AppPlatform, RecentAppItem, RecentAppsProps } from "./types";

const PLATFORM_ICON: Record<
  AppPlatform,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  google: GoogleIcon,
  apple: AppleIcon,
};

const AppLogo: React.FC<{ icon?: React.ReactNode; name: string }> = ({
  icon,
  name,
}) => {
  if (typeof icon === "string" && icon) {
    return <img className={styles.logo} src={icon} alt="" />;
  }
  if (icon) {
    return <span className={styles.logo}>{icon}</span>;
  }
  return (
    <span className={cs(styles.logo, styles.logoFallback)}>
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
};

const AppRow: React.FC<{
  app: RecentAppItem;
  platformIcon?: React.ReactNode;
}> = ({ app, platformIcon }) => {
  const active = app.activeOrders > 0;
  const Platform = app.platform ? PLATFORM_ICON[app.platform] : null;

  return (
    <div
      className={cs(styles.row, { [styles.clickable]: !!app.onClick })}
      onClick={app.onClick}
      role={app.onClick ? "button" : undefined}
    >
      <AppLogo icon={app.icon} name={app.name} />
      <span className={styles.name}>{app.name}</span>
      <span
        className={cs(styles.chip, active ? styles.chipActive : styles.chipNeutral)}
      >
        {active ? (
          <>
            <b className={styles.chipNum}>{app.activeOrders}</b>
            {" active orders"}
          </>
        ) : (
          "No active orders"
        )}
      </span>
      <span className={styles.platform}>
        {platformIcon ??
          (Platform ? <Platform className={styles.platformIcon} /> : null)}
      </span>
    </div>
  );
};

/**
 * Recent-apps card (Figma 568:75375). Pure presentational: a list of app rows
 * (logo + name + active-orders chip + platform icon) or an empty-state
 * message. White card surface flips in dark.
 */
export const RecentApps: React.FC<RecentAppsProps> = ({
  apps,
  onSeeAll,
  seeAllSlot,
  renderPlatformIcon,
  emptyText = "No apps. They will be displayed here",
  className,
}) => {
  const seeAll =
    seeAllSlot ??
    (onSeeAll ? (
      <button type="button" className={styles.seeAll} onClick={onSeeAll}>
        See all
      </button>
    ) : null);

  return (
    <div className={cs(styles.card, className)}>
      <div className={styles.head}>
        <div className={styles.title}>
          <span className={styles.iconBox}>
            <AppsIcon className={styles.icon20} />
          </span>
          <h3 className={styles.heading}>Recent apps</h3>
        </div>
        {seeAll}
      </div>

      {apps.length ? (
        <div className={styles.list}>
          {apps.map((a) => (
            <AppRow key={a.id} app={a} platformIcon={renderPlatformIcon?.(a)} />
          ))}
        </div>
      ) : (
        <div className={styles.empty}>
          <p className={styles.emptyText}>{emptyText}</p>
        </div>
      )}
    </div>
  );
};

export default RecentApps;
